'use client';

import React, { useState, useEffect } from 'react';
import CompanyIntroduction from './CompanyIntroduction';
import CompanyStory from './CompanyStory';
import TrustSignals from './TrustSignals';
import CompanyMetrics from './CompanyMetrics';
import ContactCredentials from './ContactCredentials';
import { supabaseFetch } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface StoryPoint {
  year: string;
  title: string;
  description: string;
  icon: string;
}

interface Partnership {
  name: string;
  logo: string;
  alt: string;
  category: string;
}

interface Certification {
  title: string;
  issuer: string;
  icon: string;
}

interface Metric {
  value: string;
  label: string;
  icon: string;
  suffix?: string;
}

interface TeamMember {
  name: string;
  position: string;
  image: string;
  alt: string;
  bio: string;
  expertise: string[];
}

interface ContactInfo {
  type: string;
  value: string;
  icon: string;
  link?: string;
}

interface BusinessCredential {
  label: string;
  value: string;
  icon: string;
}

const AboutUsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
    fetchPartnersFromSupabase();
  }, []);

  const fetchPartnersFromSupabase = async () => {
    try {
      const { data, error } = await supabaseFetch('partners', {}, 'id,name,logo_url,category_id');

      if (error || !data) {
        console.error('Error fetching partners:', error);
        setLoading(false);
        return;
      }

      // Fetch categories separately to get names
      const { data: cats } = await supabaseFetch('categories', {}, 'id,name');
      const catMap: Record<string, string> = {};
      cats?.forEach((c: any) => { catMap[c.id] = c.name; });

      const mappedPartnerships: Partnership[] = data.map((partner: any) => ({
        name: partner.name,
        logo: partner.logo_url,
        alt: `${partner.name} logo`,
        category: catMap[partner.category_id] || 'عام'
      }));
      setPartnerships(mappedPartnerships);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const companyData = {
    title: 'من نحن',
    titleEn: 'About Us',
    description:
    'إبداع الحرفة للتسويق هي شركة سعودية رائدة في مجال بطاقات الاشتراك والخصومات، نسعى لتوفير أفضل العروض والخصومات لعملائنا من خلال شبكة واسعة من الشركاء في مختلف القطاعات.',
    descriptionEn:
    'Ibdaa Al Herfa for Marketing is a leading Saudi company in subscription cards and discounts, striving to provide the best offers and discounts to our customers through a wide network of partners in various sectors.',
    mission:
    'تقديم قيمة حقيقية لعملائنا من خلال توفير خصومات مميزة وشراكات استراتيجية مع أفضل الأسماء التجارية في المملكة، مع الحفاظ على أعلى معايير الجودة والخدمة.',
    missionEn:
    'Delivering real value to our customers through distinctive discounts and strategic partnerships with the best commercial names in the Kingdom, while maintaining the highest standards of quality and service.',
    vision:
    'أن نكون الخيار الأول للعائلات السعودية في الحصول على خصومات وعروض حصرية، وبناء شبكة شراكات تجارية تخدم جميع احتياجات الحياة اليومية.',
    visionEn:
    'To be the first choice for Saudi families in obtaining exclusive discounts and offers, and building a commercial partnership network that serves all daily life needs.'
  };

  const storyPoints: StoryPoint[] = [
  {
    year: '2024',
    title: t('التأسيس', 'Founding'),
    description:
    t(
      'بدأت رحلتنا بفكرة بسيطة: توفير خصومات حقيقية للعائلات السعودية',
      'Our journey began with a simple idea: providing real discounts for Saudi families'
    ),
    icon: 'SparklesIcon'
  },
  {
    year: '2024',
    title: t('التوسع', 'Expansion'),
    description: t('وصلنا إلى 50+ شريك تجاري في مختلف القطاعات', 'We reached 50+ business partners in various sectors'),
    icon: 'ChartBarIcon'
  },
  {
    year: '2025',
    title: t('النمو', 'Growth'),
    description: t('تجاوزنا 2000+ عميل راضٍ عن خدماتنا', 'We exceeded 2000+ satisfied customers'),
    icon: 'UserGroupIcon'
  },
  {
    year: '2026',
    title: t('الريادة', 'Leadership'),
    description: t('أصبحنا الخيار الأول لبطاقات الخصومات في المملكة', 'We became the first choice for discount cards in the Kingdom'),
    icon: 'TrophyIcon'
  }];


  const mainStory = t(
    'تأسست إبداع الحرفة للتسويق من إيمان عميق بأن كل عائلة سعودية تستحق الحصول على أفضل الخدمات بأسعار مناسبة. بدأنا رحلتنا بفريق صغير وحلم كبير، واليوم نفخر بشبكة واسعة من الشركاء والعملاء الذين يثقون بنا.',
    'Ibdaa Al Herfa for Marketing was founded from a deep belief that every Saudi family deserves the best services at reasonable prices. We started our journey with a small team and a big dream, and today we are proud of a wide network of partners and customers who trust us.'
  );

  const foundingPrinciples = [
  t('الشفافية الكاملة في جميع تعاملاتنا مع العملاء والشركاء', 'Full transparency in all our dealings with customers and partners'),
  t('تقديم قيمة حقيقية من خلال خصومات فعلية وليست وهمية', 'Providing real value through actual discounts, not fictitious ones'),
  t('بناء علاقات طويلة الأمد مع شركائنا التجاريين', 'Building long-term relationships with our business partners'),
  t('الاستماع لاحتياجات عملائنا والتطور المستمر', 'Listening to our customers\' needs and continuous development'),
  t('الالتزام بأعلى معايير الجودة في الخدمة', 'Commitment to the highest standards of quality in service'),
  t('دعم الاقتصاد المحلي من خلال الشراكات مع الأعمال السعودية', 'Supporting the local economy through partnerships with Saudi businesses'),
];


  const certifications: Certification[] = [
  {
    title: t('سجل تجاري معتمد', 'Approved Commercial Registration'),
    issuer: t('وزارة التجارة السعودية', 'Saudi Ministry of Commerce'),
    icon: 'DocumentCheckIcon'
  },
  {
    title: t('شهادة الجودة ISO 9001', 'ISO 9001 Quality Certificate'),
    issuer: t('المنظمة الدولية للمعايير', 'International Organization for Standardization'),
    icon: 'ShieldCheckIcon'
  },
  {
    title: t('عضوية الغرفة التجارية', 'Chamber of Commerce Membership'),
    issuer: t('الغرفة التجارية بالرياض', 'Riyadh Chamber of Commerce'),
    icon: 'BuildingOfficeIcon'
  },
  {
    title: t('ترخيص التسويق الإلكتروني', 'E-Marketing License'),
    issuer: t('هيئة الاتصالات وتقنية المعلومات', 'Communications and Information Technology Commission'),
    icon: 'GlobeAltIcon'
  },
  {
    title: t('شهادة حماية البيانات', 'Data Protection Certificate'),
    issuer: t('المركز الوطني للأمن السيبراني', 'National Cybersecurity Center'),
    icon: 'LockClosedIcon'
  },
  {
    title: t('اعتماد خدمة العملاء', 'Customer Service Accreditation'),
    issuer: t('الهيئة السعودية للمواصفات والمقاييس', 'Saudi Standards, Metrology and Quality Organization'),
    icon: 'ChatBubbleLeftRightIcon'
  }];


  const metrics: Metric[] = [
  {
    value: '50',
    label: t('شريك تجاري', 'Business Partners'),
    icon: 'BuildingStorefrontIcon',
    suffix: '+'
  },
  {
    value: '2000',
    label: t('عميل راضٍ', 'Satisfied Customers'),
    icon: 'UserGroupIcon',
    suffix: '+'
  },
  {
    value: '35',
    label: t('متوسط الخصم', 'Average Discount'),
    icon: 'ReceiptPercentIcon',
    suffix: '%'
  },
  {
    value: '98',
    label: t('رضا العملاء', 'Customer Satisfaction'),
    icon: 'StarIcon',
    suffix: '%'
  }];


  const teamMembers: TeamMember[] = [
  {
    name: 'أحمد العتيبي',
    position: t('المدير التنفيذي', 'CEO'),
    image:
    "https://img.rocket.new/generatedImages/rocket_gen_img_1c6bcc831-1763295074920.png",
    alt: 'Professional Saudi businessman in white thobe and traditional headwear smiling at camera',
    bio: t('خبرة تزيد عن 15 عامًا في مجال التسويق والشراكات التجارية في المملكة', 'Over 15 years of experience in marketing and business partnerships in the Kingdom'),
    expertise: [t('التسويق الاستراتيجي', 'Strategic Marketing'), t('إدارة الشراكات', 'Partnership Management'), t('تطوير الأعمال', 'Business Development')]
  },
  {
    name: 'فاطمة السالم',
    position: t('مديرة العمليات', 'Operations Manager'),
    image:
    "https://img.rocket.new/generatedImages/rocket_gen_img_11db4a0cb-1768567885457.png",
    alt: 'Professional Saudi businesswoman in black abaya with confident smile in modern office',
    bio: t('متخصصة في تحسين العمليات وإدارة علاقات العملاء بكفاءة عالية', 'Specialist in process improvement and customer relationship management with high efficiency'),
    expertise: [t('إدارة العمليات', 'Operations Management'), t('خدمة العملاء', 'Customer Service'), t('تحسين الأداء', 'Performance Improvement')]
  },
  {
    name: 'خالد المطيري',
    position: t('مدير الشراكات', 'Partnerships Manager'),
    image:
    "https://img.rocket.new/generatedImages/rocket_gen_img_1fec87729-1763295737673.png",
    alt: 'Young Saudi professional in business suit with beard looking at camera confidently',
    bio: t('خبير في بناء العلاقات التجارية وتطوير شبكة الشركاء', 'Expert in building business relationships and developing the partner network'),
    expertise: [t('تطوير الشراكات', 'Partnership Development'), t('التفاوض', 'Negotiation'), t('إدارة العلاقات', 'Relationship Management')]
  }];


  const contactInfo: ContactInfo[] = [
  {
    type: t('رقم الهاتف', 'Phone Number'),
    value: '+966590041741',
    icon: 'PhoneIcon',
    link: 'tel:+966590041741'
  },
  {
    type: t('البريد الإلكتروني', 'Email'),
    value: 'info@creativecraftsa.com',
    icon: 'EnvelopeIcon',
    link: 'mailto:info@creativecraftsa.com'
  },
  {
    type: t('العنوان', 'Address'),
    value: t('خميس مشيط - حي الخالدية', 'Khamis Mushait - Al Khalidiyah'),
    icon: 'MapPinIcon'
  },
  {
    type: t('ساعات العمل', 'Working Hours'),
    value: t('السبت - الخميس: 9:00 ص - 6:00 م', 'Saturday - Thursday: 9:00 AM - 6:00 PM'),
    icon: 'ClockIcon'
  }];


  const businessCredentials: BusinessCredential[] = [
  {
    label: t('رقم السجل التجاري', 'Commercial Registration Number'),
    value: '1009090220',
    icon: 'DocumentTextIcon'
  },
  {
    label: t('تاريخ التأسيس', 'Founding Date'),
    value: '27/08/2024',
    icon: 'CalendarIcon'
  }];


  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-6 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-muted rounded-lg"></div>
            <div className="h-96 bg-muted rounded-lg"></div>
            <div className="h-64 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-12 space-y-12">
        <CompanyIntroduction
          title={companyData.title}
          titleEn={companyData.titleEn}
          description={companyData.description}
          descriptionEn={companyData.descriptionEn}
          mission={companyData.mission}
          missionEn={companyData.missionEn}
          vision={companyData.vision}
          visionEn={companyData.visionEn} />


        <CompanyStory
          storyPoints={storyPoints}
          mainStory={mainStory}
          foundingPrinciples={foundingPrinciples} />


        <TrustSignals
          partnerships={partnerships}
          certifications={certifications} />


        <CompanyMetrics metrics={metrics} />

        <ContactCredentials
          contactInfo={contactInfo}
          businessCredentials={businessCredentials} />

      </div>
    </div>);

};

export default AboutUsInteractive;